// Libs
import React from 'react';
import { Box, Typography } from '@mui/material';

// Components
import Rating from '../../../../../Rating';
import OutOfStockLabel from '../../../../Labels/OutOfStockLabel';
import CountComponent from '../../CountComponent';
import CloseComponent from '../../CloseComponent';
import OrderProductQtyLabels from '../../../../../Labels/OrderProductQtyLabels';
import ApprovalLabel from '../../../../../Labels/ApprovalLabel';
import ReportableIcon from '../../../../../Products/Card/ReportableIcon';

const getTitleWidth = (owner, isRecurring) => {
  if (owner) {
    return undefined;
  }
  return isRecurring ? 'calc(100% - 40px)' : '100%';
};

const DesktopView = ({
  product,
  order,
  orderItem,
  isRecurring,
  addressesCount,
  isDesktopMode,
  isMobileMode,
  isUnavailableForOrder,
  unavailableLabel,
  remainingMonthQuantity,
  classes,
  cx,
  onOpenModal,
  totalOrdered,
  piRequiredText,
}) => {
  const {
    product_id,
    brand_category,
    unit_measure,
    product_available_qty,
    order_limit,
    product_description: description,
    prepack_quantity,
    owner,
    rating: isRatable,
    reportable: isReportable,
    size_vers: sizeVers,
  } = product || {};

  const renderTitleBlock = () => (
    <>
      <Typography
        variant="h3"
        onClick={onOpenModal}
        className={cx(classes.productDescription, classes.clickable)}
        data-testid="order-product-name"
        width={getTitleWidth(owner, isRecurring)}>
        {description}
      </Typography>
      <Typography variant="textSm">{sizeVers}</Typography>
    </>
  );

  const renderHeader = () =>
    owner ? (
      <>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          width={isRecurring ? 'calc(100% - 40px)' : '100%'}
          position="relative">
          <span className={classes.audienceLabel}>{owner}</span>
          {isRatable && <Rating product={product} />}
          {isRecurring && (
            <CloseComponent
              className={classes.closeIcon}
              orderItem={orderItem}
              order={order}
              product={product}
            />
          )}
          {!isRecurring && isDesktopMode && (
            <ApprovalLabel
              orderItemStatus={orderItem.status}
              approver={orderItem.approval?.approver}
              isModified={orderItem.qty_ordered !== orderItem.qty_approved}
              align="right"
              className={classes.approvalLabel}
            />
          )}
        </Box>
        <Box mb={2}>{renderTitleBlock()}</Box>
      </>
    ) : (
      <Box
        display="flex"
        alignItems="start"
        justifyContent="space-between"
        my={2}>
        <div>{renderTitleBlock()}</div>
        {isRatable && <Rating product={product} />}
      </Box>
    );

  const renderQtyColumn = () => (
    <Box width="19%" className={classes.listCardQtyInfo}>
      <Box display="flex" className={classes.inStockQty}>
        <Typography className={classes.productInfoHeader}>
          Per Unit Qty:&nbsp;
        </Typography>
        <Typography className={classes.productInfoText}>
          {prepack_quantity ? prepack_quantity : 'N/A'}
        </Typography>
      </Box>
      {order_limit !== null && (
        <Box display="flex">
          <Typography className={classes.productInfoHeader} mb={2}>
            Max Allowed Qty:&nbsp;
          </Typography>
          <Typography className={classes.productInfoText} mb={2}>
            {order_limit}
          </Typography>
        </Box>
      )}
      {!isUnavailableForOrder ? (
        <Box display="flex">
          <Typography className={classes.productInfoHeader}>
            In Stock:&nbsp;
          </Typography>
          <Typography className={classes.productInfoText}>
            {product_available_qty}
          </Typography>
        </Box>
      ) : (
        <OutOfStockLabel label={unavailableLabel} device={{ isMobileMode }} />
      )}
      {remainingMonthQuantity !== null && remainingMonthQuantity >= 0 && (
        <Typography className={classes.productInfoHeader}>
          {`${remainingMonthQuantity} Left In Month`}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      pt="10px"
      pl={4}
      pr={2}
      pb={2}
      width="100%"
      height="100%"
      position="relative">
      {renderHeader()}
      <Box className={classes.productInfo} display="flex" gap={2}>
        <Box width="16%" className={classes.listCardQtyInfo}>
          <Typography className={classes.productInfoHeader}>
            Stock Number:
          </Typography>
          <Typography className={classes.productInfoText} mb={2}>
            {product_id}
          </Typography>
          <Typography className={classes.productInfoHeader}>
            Brand Category:
          </Typography>
          <Typography className={classes.productInfoText}>
            {prepack_quantity ? brand_category : 'N/A'}
          </Typography>
        </Box>
        <Box width="14%" className={classes.listCardQtyInfo}>
          <Typography className={classes.productInfoHeader}>
            Unit Measure:
          </Typography>
          <Typography className={classes.productInfoText} mb={2}>
            {prepack_quantity ? unit_measure : 'N/A'}
          </Typography>
          <Typography className={classes.productInfoHeader}>
            PI Required:
          </Typography>
          <Typography className={classes.productInfoText}>
            {piRequiredText}
          </Typography>
        </Box>
        {renderQtyColumn()}
        <Box width="24%" className={classes.qtyInfo}>
          <div className={classes.qtyInfoText}>
            <OrderProductQtyLabels
              orderItem={orderItem}
              addressesCount={addressesCount}
              isRecurring={isRecurring}
            />
          </div>
          <Typography className={cx(classes.qtyInfoText, classes.totalItems)}>
            {`Total Items Ordered: ${prepack_quantity ? totalOrdered : 'N/A'}`}
          </Typography>
        </Box>
        {isRecurring && !isUnavailableForOrder && (
          <Box className={classes.buttonContainer}>
            <CountComponent
              product={product}
              orderItem={orderItem}
              order={order}
            />
          </Box>
        )}
        {isReportable && (
          <Box ml="auto">
            <ReportableIcon />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DesktopView;
